import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/pdq87uby_cropped-LOGOS-IR-1.png";

const translations = {
  overline: { es: "Sobre la academia", en: "About the Academy" },
  title: { es: "Somos Industria de Reuniones", en: "We Are Industria de Reuniones" },
  badges: {
    es: ["+20 años de experiencia", "Presencia en toda Latinoamérica", "Marco educativo global", "6 cursos especializados"],
    en: ["+20 years of experience", "Presence throughout Latin America", "Global education framework", "6 specialized courses"],
  },
  associatedWith: { es: "Asociada a", en: "Associated with" },
  paragraphs: {
    es: [
      "Somos una <strong>academia especializada</strong> en la formación y desarrollo profesional en la industria de reuniones (MICE) en Latinoamérica.",
      "Contamos con más de <strong>20 años de experiencia</strong> impulsando el crecimiento del sector turístico y de eventos, trabajando con destinos, empresas y profesionales de toda la región.",
      "Industria de Reuniones es una marca asociada a <strong>Incubatour</strong>, enfocada en la innovación, el desarrollo de productos turísticos y la profesionalización del sector.",
      "Nuestro equipo está liderado por expertos reconocidos: <strong>Eduardo Yarto</strong>, con amplia trayectoria en turismo y desarrollo de destinos; <strong>Tony Yarto</strong>, especialista en estrategia e innovación MICE; y <strong>Paola López</strong>, experta en inteligencia artificial aplicada.",
      "<strong>Nuestro compromiso: elevar el nivel de la industria en la región y generar oportunidades reales de crecimiento profesional.</strong>",
    ],
    en: [
      "We are a <strong>specialized academy</strong> in training and professional development in the meetings industry (MICE) in Latin America.",
      "We have more than <strong>20 years of experience</strong> driving the growth of the tourism and events sector, working with destinations, companies, and professionals throughout the region.",
      "Industria de Reuniones is a brand associated with <strong>Incubatour</strong>, focused on innovation, tourism product development, and sector professionalization.",
      "Our team is led by recognized experts: <strong>Eduardo Yarto</strong>, with extensive experience in tourism and destination development; <strong>Tony Yarto</strong>, specialist in MICE strategy and innovation; and <strong>Paola López</strong>, expert in applied artificial intelligence.",
      "<strong>Our commitment: to elevate the level of the industry in the region and generate real opportunities for professional growth.</strong>",
    ],
  },
};

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const badges = t(translations.badges);
  const paragraphs = t(translations.paragraphs);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="py-24 md:py-32 bg-[#EEF1F4]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }} className="lg:col-span-5">
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <img src={LOGO_URL} alt="Industria de Reuniones — academia MICE LATAM" width="240" height="80" decoding="async" className="h-16 md:h-20 mb-6" />
                <div className="space-y-4">
                  {badges.map((badge, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#2ED3C6] rounded-full" />
                      <span className="font-body text-[#0B1F3B]/70 text-sm">{badge}</span>
                    </div>
                  ))}
                </div>
              </div>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay: 0.5 }} className="absolute -bottom-4 -right-4 bg-[#0B1F3B] text-white p-4 rounded-xl shadow-xl">
                <div className="text-xs font-body text-white/60 mb-1">{t(translations.associatedWith)}</div>
                <div className="font-display text-lg font-semibold">Incubatour</div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
              <span className="w-8 h-[2px] bg-[#2ED3C6]" />
              {t(translations.overline)}
            </span>
            <h2 data-testid="about-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-8">
              {t(translations.title)}
            </h2>
            <div className="space-y-6 font-body text-[#0B1F3B]/70 text-base md:text-lg leading-relaxed">
              {paragraphs.map((paragraph, index) => (
                <p key={index} dangerouslySetInnerHTML={{ __html: paragraph.replace(/<strong>/g, '<strong class="text-[#0B1F3B]">') }} />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
